import React, { useMemo, useState } from 'react';
import './QuickTopUpPopup.css';
import { useTranslation } from 'react-i18next';
import { startPiTopUp } from '../../utils/piTopUp';

import walletIcon from '../../assets/mywallet-icon.png';
import cartIcon from '../../assets/cart-icon.png';
import tipIcon from '../../assets/tip-icon.png';
import stakeIcon from '../../assets/stakebadge-icon.png';
import plansIcon from '../../assets/plans-icon.png';
import mystoreIcon from '../../assets/mystore-icon.png';

function QuickTopUpPopup({
  open,
  user,
  onClose,
  onSuccess,
  onWalletRefresh,
  title,
  subtitle,
  minAmount = 0.1,
}) {
  const { t } = useTranslation();

  // Clean preset row: 0.1, 0.5, 1, Custom
  const amountOptions = useMemo(() => [0.1, 0.5, 1], []);

  const defaultAmount = amountOptions.includes(Number(minAmount))
    ? Number(minAmount)
    : 0.1;

  const [selectedAmount, setSelectedAmount] = useState(defaultAmount);
  const [customMode, setCustomMode] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  if (!open) return null;

  const popupTitle = title || t('quick_topup.title', 'Top up your wallet');

  const popupSubtitle =
    subtitle ||
    t(
      'quick_topup.subtitle',
      'Add Pi to your BarterFi wallet and continue instantly.'
    );

  const featureItems = [
    {
      icon: cartIcon,
      label: t('quick_topup.features.inapp_purchases', 'In-app purchases'),
    },
    {
      icon: mystoreIcon,
      label: t('quick_topup.features.marketplace', 'Marketplace'),
    },
    {
      icon: tipIcon,
      label: t('quick_topup.features.tips', 'Tips'),
    },
    {
      icon: stakeIcon,
      label: t('quick_topup.features.stakes', 'Stakes'),
    },
    {
      icon: plansIcon,
      label: t('quick_topup.features.subscriptions', 'Subscriptions'),
    },
  ];

  const formatAmountLabel = (amount) => {
    const numericAmount = Number(amount);

    if (Number.isNaN(numericAmount)) return amount;

    return numericAmount % 1 === 0
      ? String(numericAmount)
      : numericAmount.toFixed(1);
  };

  const cleanCustomAmount = (value) => {
    // Allow only numbers and one decimal point.
    const cleaned = value
      .replace(',', '.')
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1');

    setCustomAmount(cleaned);

    const numericValue = Number(cleaned);

    if (!Number.isNaN(numericValue) && numericValue > 0) {
      setSelectedAmount(numericValue);
    }
  };

  const openCustomMode = () => {
    setCustomMode(true);
    setErrorMsg('');
    setSuccessMsg('');
    setCustomAmount('');
  };

  const closeCustomMode = () => {
    setCustomMode(false);
    setCustomAmount('');
    setErrorMsg('');
    setSelectedAmount(defaultAmount);
  };

  const handlePresetAmount = (amount) => {
    setCustomMode(false);
    setCustomAmount('');
    setErrorMsg('');
    setSelectedAmount(amount);
  };

  const getAmountForTopUp = () => {
    if (!customMode) return Number(selectedAmount);

    const numericCustomAmount = Number(customAmount);

    if (
      !customAmount ||
      Number.isNaN(numericCustomAmount) ||
      numericCustomAmount <= 0
    ) {
      return null;
    }

    return numericCustomAmount;
  };

  const handleTopUp = async () => {
    if (loading) return;

    if (!user?.username) {
      setErrorMsg(t('quick_topup.errors.missing_user', 'User not found.'));
      return;
    }

    const topUpAmount = getAmountForTopUp();

    if (!topUpAmount) {
      setSuccessMsg('');
      setErrorMsg(
        t(
          'quick_topup.errors.invalid_amount',
          'Please enter a valid Pi amount.'
        )
      );
      return;
    }

    if (topUpAmount < Number(minAmount)) {
      setSuccessMsg('');
      setErrorMsg(
        t('quick_topup.errors.min_amount', {
          defaultValue: `Minimum top up is ${Number(minAmount)} Pi.`,
          amount: Number(minAmount),
        })
      );
      return;
    }

    try {
      setLoading(true);
      setErrorMsg('');
      setSuccessMsg('');

      const result = await startPiTopUp({
        amount: topUpAmount,
        username: user.username,
        t,
        onWalletRefresh,
        onRecovered: (recovered) => {
          setErrorMsg('');
          setSuccessMsg(recovered.message || t('topup.recovered'));
        },
        onSuccess: (success) => {
          setErrorMsg('');
          setSuccessMsg(
            success.message ||
              t('topup.success', { amount: Number(topUpAmount) })
          );

          window.setTimeout(() => {
            if (onSuccess) onSuccess(success);
          }, 500);
        },
        onCancel: (cancelled) => {
          setSuccessMsg('');
          setErrorMsg(cancelled.message || t('topup.errors.cancelled'));
        },
        onError: (error) => {
          setSuccessMsg('');
          setErrorMsg(
            error.message ||
              t('topup.errors.generic', { msg: 'Unknown error' })
          );
        },
      });

      if (result?.ok) {
        window.setTimeout(() => {
          if (onClose) onClose();
        }, 700);
      }
    } catch (err) {
      console.error('❌ QuickTopUpPopup top-up failed:', err);
      setSuccessMsg('');
      setErrorMsg(
        t('topup.errors.generic', {
          msg: err?.message || 'Unknown error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = () => {
    if (!loading && onClose) onClose();
  };

  const topUpButtonLabel = loading
    ? t('quick_topup.loading', 'Starting...')
    : t('quick_topup.button_with_amount', {
        defaultValue: `Top Up ${formatAmountLabel(selectedAmount || 0)} Pi`,
        amount: formatAmountLabel(selectedAmount || 0),
      });

  return (
    <div className="quick-topup-popup-backdrop" onClick={handleBackdropClick}>
      <div
        className="quick-topup-popup-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        {errorMsg && (
          <div className="quick-topup-popup-banner error">{errorMsg}</div>
        )}

        {successMsg && (
          <div className="quick-topup-popup-banner success">{successMsg}</div>
        )}

        <div className="quick-topup-popup-bg-shape quick-topup-popup-bg-shape--top" />
        <div className="quick-topup-popup-bg-shape quick-topup-popup-bg-shape--bottom" />

        <div className="quick-topup-popup-inner">
          <div className="quick-topup-popup-top">
            <div className="quick-topup-popup-icon-wrap">
              <img
                src={walletIcon}
                alt="Wallet"
                className="quick-topup-popup-icon"
              />
            </div>

            <h2 className="quick-topup-popup-title">{popupTitle}</h2>

            <p className="quick-topup-popup-subtitle">{popupSubtitle}</p>
          </div>

          <div className="quick-topup-popup-middle">
            <div className="quick-topup-popup-amount-card">
              <div className="quick-topup-popup-amount-head">
                <div className="quick-topup-popup-amount-label">
                  {t('quick_topup.select_amount', 'Select amount')}
                </div>

                {customMode && (
                  <button
                    type="button"
                    className="quick-topup-popup-custom-close"
                    onClick={closeCustomMode}
                    disabled={loading}
                    aria-label="Close custom amount"
                  >
                    ×
                  </button>
                )}
              </div>

              {!customMode ? (
                <div className="quick-topup-popup-amount-row">
                  {amountOptions.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      className={`quick-topup-popup-amount-pill ${
                        Number(selectedAmount) === Number(amount)
                          ? 'active'
                          : ''
                      }`}
                      onClick={() => handlePresetAmount(amount)}
                      disabled={loading}
                    >
                      {formatAmountLabel(amount)}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="quick-topup-popup-amount-pill custom"
                    onClick={openCustomMode}
                    disabled={loading}
                  >
                    {t('quick_topup.custom', 'Custom')}
                  </button>
                </div>
              ) : (
                <div className="quick-topup-popup-custom-box">
                  <div className="quick-topup-popup-custom-input-wrap">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="quick-topup-popup-custom-input"
                      placeholder={t(
                        'quick_topup.custom_placeholder',
                        'Enter amount e.g. 100'
                      )}
                      value={customAmount}
                      onChange={(e) => cleanCustomAmount(e.target.value)}
                      disabled={loading}
                      autoFocus
                    />

                    <span className="quick-topup-popup-custom-suffix">Pi</span>
                  </div>

                  <div className="quick-topup-popup-custom-helper">
                    {t(
                      'quick_topup.custom_helper',
                      'You can enter amounts like 32, 35.6, or 100.'
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="quick-topup-popup-features-card">
              <div className="quick-topup-popup-features-grid">
                {featureItems.map((item) => (
                  <div
                    className="quick-topup-popup-feature-item"
                    key={item.label}
                  >
                    <div className="quick-topup-popup-feature-icon-wrap">
                      <img
                        src={item.icon}
                        alt={item.label}
                        className="quick-topup-popup-feature-icon"
                      />
                    </div>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="quick-topup-popup-bottom">
            <button
              type="button"
              className="quick-topup-popup-primary-btn"
              onClick={handleTopUp}
              disabled={loading}
            >
              {topUpButtonLabel}
            </button>

            <button
              type="button"
              className="quick-topup-popup-pass-btn"
              onClick={onClose}
              disabled={loading}
            >
              {t('quick_topup.pass_button', "I'll pass this time")}
            </button>

            <div className="quick-topup-popup-footnote">
              {t(
                'quick_topup.footnote',
                'Use your BarterFi wallet for purchases, tips, staking, and subscriptions.'
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuickTopUpPopup;
